import axios from "axios";
import config from "../config";

const refreshClient = axios.create({ baseURL: config.SERVER_URL });
let refreshPromise = null;

export function notifyAuthChange() {
    window.dispatchEvent(new Event("auth-token-changed"));
}

function clearSession() {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    notifyAuthChange();
}

export function installAuthentication(client) {
    client.interceptors.request.use((request) => {
        const token = localStorage.getItem("token");
        if (token) request.headers.Authorization = `Bearer ${token}`;
        return request;
    });
    client.interceptors.response.use((response) => response, async (error) => {
        const original = error.config;
        if (error.response?.status !== 401 || !original || original._retry
            || /\/api\/(login|refresh|reset-password|register)(?:[/?]|$)/.test(original.url || "")) {
            throw error;
        }
        original._retry = true;
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
            clearSession();
            throw error;
        }
        // Another request may already have refreshed while this one was in flight.
        const currentToken = localStorage.getItem("token");
        if (currentToken && original.headers.Authorization !== `Bearer ${currentToken}`) {
            original.headers.Authorization = `Bearer ${currentToken}`;
            return client(original);
        }
        if (!refreshPromise) {
            refreshPromise = refreshClient.post("/api/refresh", { refreshToken })
                .then(({ data }) => {
                    // Do not resurrect a session after logout or an account switch.
                    if (localStorage.getItem("refreshToken") !== refreshToken) throw error;
                    localStorage.setItem("token", data.token);
                    localStorage.setItem("refreshToken", data.refreshToken);
                    notifyAuthChange();
                    return data.token;
                })
                .catch((refreshError) => {
                    if (localStorage.getItem("refreshToken") === refreshToken) clearSession();
                    throw refreshError;
                })
                .finally(() => { refreshPromise = null; });
        }
        const token = await refreshPromise;
        original.headers.Authorization = `Bearer ${token}`;
        return client(original);
    });
    return client;
}

export function createAuthenticatedClient(options) {
    return installAuthentication(axios.create(options));
}

// Existing pages use the default Axios client; give them the same refresh path.
axios.defaults.baseURL = config.SERVER_URL;
export default installAuthentication(axios);
